// frontend/src/pages/RespondQuotation.js  (replacement)
//
// The supplier's devis, built to match WorkshopQuotationInvoiceForm: chrome/ink
// panels, one inline-editable line table, and — the important part — the client
// price shown beside the supplier's own, so the supplier can see exactly what
// the client will be charged before sending.
//
// Two substantive changes beyond the styling:
//
//  1. It posts to POST /quotations/:rfqId/respond. It used to post to
//     /spare-parts/:id/respond, whose handler builds a QuotationResponse out of
//     fields the schema does not have (requestId/supplierId/partsOffered rather
//     than rfq/supplier/items), so `rfq` and `supplier` came out missing and
//     the required-field validation rejected every submission. No devis sent
//     from this page could ever have been saved.
//
//  2. Line prices are sent as `supplierPrice`. The old payload used `price`,
//     which the pricing code does not read — so even had it saved, every line
//     would have been priced at zero.

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

import API_URL from '../api/baseURL';
import VehicleIdentityCard from '../components/VehicleIdentityCard';
import {
  Page, Panel, Button, Field, EmptyState, ErrorNote, Note,
  inputClass, textareaClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

// Mirrors config/platformFees.PLATFORM_FEE_PCT. The server is authoritative —
// this only powers the preview, so the supplier is not surprised by the number
// the client sees.
const PLATFORM_FEE_PCT = 0.10;
const clientPrice = (supplierPrice) => Math.round((Number(supplierPrice) || 0) * (1 + PLATFORM_FEE_PCT));

const emptyLine = () => ({ description: '', reference: '', brand: '', quantity: 1, supplierPrice: 0 });

// The car to show the supplier.
//
// `car` is populated while the vehicle exists. When it has been deleted
// populate yields null, and this page used to hand that null straight to
// VehicleIdentityCard — which renders nothing. The supplier was then asked to
// price parts with no VIN, no plate and no make on screen, while the RFQ still
// held all three in its snapshots. That is exactly what the snapshots are for.
const vehicleOf = (rfq) => {
  if (!rfq) return null;
  if (rfq.car) return rfq.car;
  const snap = rfq.vehicleSnapshot;
  if (!snap && !rfq.vinSnapshot) return null;
  return {
    _id: `snapshot-${rfq._id}`,
    brand: snap?.make,
    model: snap?.model,
    year: snap?.year,
    vin: rfq.vinSnapshot,
  };
};

const cell = 'w-full bg-transparent border border-[#241F1B] rounded-lg px-2 py-1.5 text-[12.5px] '
  + 'text-[#F4F1EA] outline-none focus:border-[#4E4740] transition-colors';

export default function RespondQuotation({ token }) {
  const { id: requestId } = useParams();
  const navigate = useNavigate();

  const [requestInfo, setRequestInfo] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(true);
  const [items, setItems] = useState([emptyLine()]);
  const [terms, setTerms] = useState({ deliveryAddress: '', deliveryETA: '', warranty: '', notes: '' });
  const [includeTax, setIncludeTax] = useState(false);

  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    if (!requestId || !token) return;
    // The route is behind authenticateUser; this call used to send no header,
    // so it 401'd and the whole request panel silently rendered nothing.
    axios.get(`${API_URL}/quotations/${requestId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setRequestInfo(res.data))
      .catch((err) => {
        console.error('Failed to fetch quotation request', err);
        setError(err.response?.status === 403
          ? "Cette demande ne vous est pas adressée."
          : "Demande introuvable ou inaccessible.");
      })
      .finally(() => setLoadingRequest(false));
  }, [requestId, token]);

  // Prefill one line per requested part, so the supplier prices the list they
  // were sent instead of retyping it.
  useEffect(() => {
    const parts = requestInfo?.partsRequested;
    if (!Array.isArray(parts) || !parts.length) return;
    setItems(parts.map((p) => ({
      description: p.name || '',
      reference: p.reference || '',
      brand: p.brand || '',
      quantity: Number(p.quantity) || 1,
      supplierPrice: 0,
    })));
  }, [requestInfo]);

  const setLine = (idx, field, value) => {
    setItems((cur) => cur.map((it, i) => i === idx
      ? { ...it, [field]: field === 'quantity' || field === 'supplierPrice' ? Number(value) || 0 : value }
      : it));
  };
  const addLine = () => setItems((cur) => [...cur, emptyLine()]);
  const removeLine = (idx) => setItems((cur) => cur.filter((_, i) => i !== idx));

  const vehicle = useMemo(() => vehicleOf(requestInfo), [requestInfo]);

  const totals = useMemo(() => {
    const supplierSubtotal = items.reduce((s, it) => s + (Number(it.supplierPrice) || 0) * (Number(it.quantity) || 0), 0);
    const clientSubtotal = items.reduce((s, it) => s + clientPrice(it.supplierPrice) * (Number(it.quantity) || 0), 0);
    const tax = includeTax ? Math.round(clientSubtotal * 0.18) : 0;
    return {
      supplierSubtotal: Math.round(supplierSubtotal),
      clientSubtotal,
      tax,
      clientTotal: clientSubtotal + tax,
      platformShare: clientSubtotal - Math.round(supplierSubtotal),
    };
  }, [items, includeTax]);

  const handleAiSuggestAll = async () => {
    if (!requestInfo) return;
    setAiLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/ai/quotation-suggestions`, {
        // Through the same fallback: the suggestions are matched on VIN, so
        // reading it off the populated car alone made them stop working for a
        // car that had since been deleted.
        vin: vehicle?.vin,
        items: requestInfo.partsRequested,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (Array.isArray(res.data) && res.data.length) {
        setItems(res.data.map((p) => ({
          description: p.description || p.name || '',
          reference: p.reference || '',
          brand: p.brand || '',
          quantity: Number(p.quantity) || 1,
          // The suggestion endpoint returns `price`; this form works in
          // supplierPrice throughout.
          supplierPrice: Number(p.supplierPrice ?? p.price) || 0,
        })));
        setOk('Suggestions appliquées — vérifiez les prix avant d’envoyer.');
      } else {
        setError('Aucune suggestion trouvée pour ce véhicule.');
      }
    } catch (err) {
      console.error(err);
      setError("La suggestion automatique a échoué.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOk('');

    const filled = items.filter((it) => it.description.trim() && Number(it.supplierPrice) > 0);
    if (!filled.length) {
      setError('Ajoutez au moins une ligne avec une désignation et un prix.');
      return;
    }
    if (!terms.deliveryAddress.trim()) {
      setError('Adresse de livraison requise.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/quotations/${requestId}/respond`, {
        // `items` with `supplierPrice` is the shape the pricing code reads.
        items: filled.map((it, idx) => ({
          description: it.description.trim(),
          reference: it.reference.trim(),
          brand: it.brand.trim(),
          quantity: Math.max(1, Number(it.quantity) || 1),
          supplierPrice: Number(it.supplierPrice) || 0,
          rfqItemIndex: idx,
        })),
        deliveryAddress: terms.deliveryAddress.trim(),
        deliveryETA: terms.deliveryETA.trim(),
        warranty: terms.warranty.trim(),
        notes: terms.notes.trim(),
        taxApplicable: includeTax,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setOk('Devis envoyé.');
      setTimeout(() => navigate('/supplier'), 1200);
    } catch (err) {
      console.error('Error submitting quotation:', err);
      setError(err.response?.data?.error || "L'envoi du devis a échoué.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page
      subtitle="Demande de devis"
      title="Répondre à la demande"
      actions={<Button onClick={() => navigate('/supplier')}>Retour</Button>}
      width="narrow"
    >
      {loadingRequest ? (
        <Panel><EmptyState title="Chargement de la demande…" /></Panel>
      ) : !requestInfo ? (
        <Panel><ErrorNote>{error || 'Demande introuvable.'}</ErrorNote></Panel>
      ) : (
        <>
          <Panel title="Véhicule concerné">
            <VehicleIdentityCard vehicle={vehicle} label="Véhicule" />
            {vehicle?.year && (
              <p className="text-[11.5px] text-[#8F877B] mt-2">Année : {vehicle.year}</p>
            )}

            {Array.isArray(requestInfo.partsRequested) && requestInfo.partsRequested.length > 0 && (
              <div className="mt-3.5 pt-3.5 border-t border-[#241F1B]">
                <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-2">
                  Pièces demandées
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {requestInfo.partsRequested.map((p, i) => (
                    <span key={i}
                      className="text-[11.5px] text-[#C9C3B6] bg-[#1B1815] border border-[#241F1B] rounded-[6px] px-2 py-[3px]">
                      {p.name} × {p.quantity || 1}
                      {p.reference && <span className="text-[#6E665C]"> · {p.reference}</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
            <Panel
              title="Votre offre"
              action={
                <div className="flex gap-2">
                  <Button as="button" type="button" onClick={handleAiSuggestAll} disabled={aiLoading}>
                    {aiLoading ? 'Recherche…' : 'Suggérer les pièces'}
                  </Button>
                  <Button as="button" type="button" onClick={addLine}>+ Ligne</Button>
                </div>
              }
            >
              <div className="rounded-xl border border-[#241F1B] overflow-x-auto">
                <table className="w-full border-collapse" style={{ minWidth: 760 }}>
                  <thead>
                    <tr className="border-b border-[#241F1B]">
                      {['Désignation', 'Référence', 'Marque', 'Qté', 'Votre P.U.', 'Total', ''].map((h, i) => (
                        <th key={i}
                          className="px-2.5 py-2.5 text-left text-[10px] uppercase tracking-[.14em] text-[#6E665C] font-normal whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-b border-[#1E1B18] last:border-0">
                        <td className="px-2.5 py-2 min-w-[190px]">
                          <input className={cell} type="text" value={item.description}
                            onChange={(e) => setLine(idx, 'description', e.target.value)} />
                        </td>
                        <td className="px-2.5 py-2 w-[120px]">
                          <input className={cell} type="text" value={item.reference}
                            onChange={(e) => setLine(idx, 'reference', e.target.value)} />
                        </td>
                        <td className="px-2.5 py-2 w-[110px]">
                          <input className={cell} type="text" value={item.brand}
                            onChange={(e) => setLine(idx, 'brand', e.target.value)} />
                        </td>
                        <td className="px-2.5 py-2 w-[70px]">
                          <input className={`${cell} text-center`} type="number" min={1} value={item.quantity}
                            onChange={(e) => setLine(idx, 'quantity', e.target.value)} />
                        </td>
                        <td className="px-2.5 py-2 w-[110px]">
                          <input className={`${cell} text-right`} type="number" min={0} value={item.supplierPrice}
                            onChange={(e) => setLine(idx, 'supplierPrice', e.target.value)} />
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap text-right">
                          <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[12.5px] text-[#F4F1EA]">
                            {fmt(item.quantity * item.supplierPrice)}
                          </span>
                          {/* What the client is charged for this line — the
                              supplier should never learn the markup from the
                              client's invoice. */}
                          <span className="block text-[10px] text-[#6E665C] font-['Space_Grotesk',sans-serif] tabular-nums mt-0.5">
                            client {fmt(clientPrice(item.supplierPrice) * (Number(item.quantity) || 0))}
                          </span>
                        </td>
                        <td className="px-2.5 py-2 text-right">
                          {items.length > 1 && (
                            <button type="button" onClick={() => removeLine(idx)}
                              className="text-[#8F877B] hover:text-[#E2574B] transition-colors p-1"
                              aria-label="Retirer la ligne">
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="1.8" strokeLinecap="round"><path d="M6 18 18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-[18px]">
                <Field label="Adresse de livraison" required>
                  <input className={inputClass} value={terms.deliveryAddress}
                    onChange={(e) => setTerms({ ...terms, deliveryAddress: e.target.value })}
                    placeholder="Ex. : Zone 4, Abidjan" required />
                </Field>
                <Field label="Délai de livraison">
                  <input className={inputClass} value={terms.deliveryETA}
                    onChange={(e) => setTerms({ ...terms, deliveryETA: e.target.value })}
                    placeholder="Ex. : 24–48 h" />
                </Field>
                <Field label="Garantie">
                  <input className={inputClass} value={terms.warranty}
                    onChange={(e) => setTerms({ ...terms, warranty: e.target.value })}
                    placeholder="Ex. : 6 mois" />
                </Field>
                <Field label="Note" className="sm:col-span-2">
                  <textarea className={textareaClass} rows={3} value={terms.notes}
                    onChange={(e) => setTerms({ ...terms, notes: e.target.value })}
                    placeholder="Disponibilité, équivalences proposées, conditions…" />
                </Field>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pt-3.5 mt-[18px] border-t border-[#241F1B]">
                <label className="flex items-center gap-2.5 text-[12.5px] text-[#C9C3B6] cursor-pointer">
                  <input type="checkbox" checked={includeTax}
                    onChange={() => setIncludeTax((v) => !v)}
                    className="w-4 h-4 accent-[#F4F1EA]" />
                  Inclure la TVA (18 %)
                </label>

                <div className="sm:ml-auto w-full sm:w-[260px] flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
                    <span className="text-[#8F877B]">Votre revenu</span>
                    <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
                      {fmt(totals.supplierSubtotal)} F
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
                    <span className="text-[#8F877B]">Commission 1PARC (10 %)</span>
                    <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#8F877B]">
                      {fmt(totals.platformShare)} F
                    </span>
                  </div>
                  {includeTax && (
                    <div className="flex items-baseline justify-between gap-6 text-[12.5px]">
                      <span className="text-[#8F877B]">TVA (18 %)</span>
                      <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#DFA23C]">
                        {fmt(totals.tax)} F
                      </span>
                    </div>
                  )}
                  <div className="pt-2 mt-1 border-t border-[#241F1B] flex items-baseline justify-between gap-6">
                    <span className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Prix client</span>
                    <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold text-[#F4F1EA]">
                      {fmt(totals.clientTotal)} <span className="text-[12px] text-[#6E665C]">F</span>
                    </span>
                  </div>
                </div>
              </div>
            </Panel>

            {error && <ErrorNote>{error}</ErrorNote>}
            {ok && <Note tone="ok">{ok}</Note>}

            <div className="flex gap-2.5">
              <Button as="button" type="submit" variant="primary" disabled={saving}>
                {saving ? 'Envoi…' : 'Envoyer le devis'}
              </Button>
              <Button as="button" type="button" onClick={() => navigate('/supplier')}>Annuler</Button>
            </div>
          </form>
        </>
      )}
    </Page>
  );
}
